<template>
  <div>
    <validation-observer ref="validationGoogleAdsRules">
      <b-tabs
        v-model="activeMainTab"
        pills
      >
        <!-- Tab 1: General Settings & Texts -->
        <b-tab>
          <template #title>
            <feather-icon
              icon="SettingsIcon"
              size="18"
              class="mr-50"
            />
            <span class="font-weight-bold">{{ $t("google_ads.main_settings") }}</span>
          </template>
          <b-card>
            <div class="head-card">
              <div class="head-card-top">
                <h4>{{ $t("google_ads.main_settings") }}</h4>
              </div>
              <hr>
            </div>
            <!-- Main Grid: Left side for settings, Right side for sticky preview -->
            <b-row>
              <!-- Left Column: All settings inputs (Title, Status, Formats, and sub-configs) -->
              <b-col
                cols="12"
                lg="7"
              >
                <!-- Row 1: Title & Status -->
                <b-row class="mb-1">
                  <!-- Title -->
                  <b-col
                    cols="12"
                    md="8"
                  >
                    <b-form-group :label="$t('google_ads.title')">
                      <validation-provider
                        #default="{ errors }"
                        :name="$t('google_ads.title')"
                        rules="required"
                      >
                        <b-form-input
                          v-model="googleAd.title"
                          :placeholder="$t('google_ads.title')"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>

                  <!-- Status -->
                  <b-col
                    cols="12"
                    md="4"
                  >
                    <b-form-group :label="$t('google_ads.status')">
                      <b-form-select
                        v-model="googleAd.status"
                        :options="statusesLocale"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>

                <!-- Row 2: Active Ad Formats -->
                <b-row class="mb-2">
                  <b-col cols="12">
                    <b-form-group :label="$t('google_ads.active_formats') || 'Активные форматы рекламы'">
                      <div
                        class="d-flex align-items-center mt-25"
                        style="gap: 16px;"
                      >
                        <b-form-checkbox v-model="googleAd.config.enableModal">
                          {{ $t("google_ads.format_modal") || "Модальное окно" }}
                        </b-form-checkbox>
                        <b-form-checkbox v-model="googleAd.config.enableInImage">
                          {{ $t("google_ads.format_in_image") || "In-Image" }}
                        </b-form-checkbox>
                      </div>
                      <b-alert
                        v-if="googleAd.config.enableInImage && !hasConfiguredInImageSlot"
                        variant="warning"
                        show
                        class="mt-1 mb-0"
                      >
                        <div class="alert-body">
                          <feather-icon
                            icon="InfoIcon"
                            class="mr-50"
                          />
                          <span>{{ $t('google_ads.in_image_slots_required') }}</span>
                          <b-button
                            variant="link"
                            size="sm"
                            class="p-0 ml-50 align-baseline"
                            @click="openInImagePlatformSettings"
                          >
                            {{ $t('google_ads.ad_platform_settings') }}
                          </b-button>
                        </div>
                      </b-alert>
                    </b-form-group>
                  </b-col>
                </b-row>

                <hr class="mb-3">

                <!-- Format Config Tabs -->
                <b-tabs
                  v-if="googleAd.config.enableModal || googleAd.config.enableInImage"
                  v-model="activeFormatTab"
                  pills
                  class="nav-tabs-custom"
                >
                  <!-- Tab A: Modal Window Settings -->
                  <b-tab
                    v-if="googleAd.config.enableModal"
                    active
                  >
                    <template #title>
                      <feather-icon
                        icon="LayersIcon"
                        size="18"
                        class="mr-50"
                      />
                      <span class="font-weight-bold">{{ $t("google_ads.format_modal") || "Модальное окно" }}</span>
                    </template>

                    <!-- Tabs for General Settings, Texts & Labels, and Styles -->
                    <b-tabs
                      ref="modalSettingsTabs"
                      class="mt-2"
                      nav-class="mb-2"
                    >
                      <!-- Tab A: General Settings (Общие настройки) -->
                      <b-tab active>
                        <template #title>
                          <feather-icon
                            icon="SettingsIcon"
                            size="18"
                            class="mr-50"
                          />
                          <span class="font-weight-bold">{{ $t("google_ads.tabs.general") }}</span>
                        </template>

                        <b-card bg-variant="light">
                          <h5>{{ $t('google_ads.display_conditions.title') }}</h5>
                          <b-form-group>
                            <b-form-checkbox
                              v-model="googleAd.config.enableUserPercentage"
                              switch
                            >
                              {{ $t('google_ads.display_conditions.enableUserPercentage') }}
                            </b-form-checkbox>
                            <small class="text-muted">{{ $t('google_ads.display_conditions.enableUserPercentage_help') }}</small>
                          </b-form-group>
                          <b-form-group
                            v-if="googleAd.config.enableUserPercentage || (displayConditionsSubmitted && displayConditionsErrors.userPercentage)"
                            :label="$t('google_ads.display_conditions.userPercentage')"
                            label-for="offerwall-userPercentage"
                          >
                            <b-input-group append="%">
                              <b-form-input
                                id="offerwall-userPercentage"
                                v-model="googleAd.config.userPercentage"
                                type="number"
                                min="0"
                                max="100"
                                step="1"
                                :state="displayConditionsErrors.userPercentage ? false : null"
                              />
                            </b-input-group>
                            <small
                              v-if="displayConditionsErrors.userPercentage"
                              class="text-danger d-block"
                            >{{ $t('google_ads.display_conditions.userPercentage_error') }}</small>
                            <small class="text-muted">{{ $t('google_ads.display_conditions.userPercentage_help') }}</small>
                          </b-form-group>
                          <b-form-group>
                            <b-form-checkbox
                              v-model="googleAd.config.enablePageCounter"
                              switch
                            >
                              {{ $t('google_ads.display_conditions.enablePageCounter') }}
                            </b-form-checkbox>
                            <small class="text-muted">{{ $t('google_ads.display_conditions.enablePageCounter_help') }}</small>
                          </b-form-group>
                          <b-form-group
                            v-if="googleAd.config.enablePageCounter || (displayConditionsSubmitted && displayConditionsErrors.pageCounterThreshold)"
                            :label="$t('google_ads.display_conditions.pageCounterThreshold')"
                            label-for="offerwall-pageCounterThreshold"
                          >
                            <b-input-group>
                              <b-form-input
                                id="offerwall-pageCounterThreshold"
                                v-model="googleAd.config.pageCounterThreshold"
                                type="number"
                                min="1"
                                step="1"
                                :state="displayConditionsErrors.pageCounterThreshold ? false : null"
                              />
                            </b-input-group>
                            <small
                              v-if="displayConditionsErrors.pageCounterThreshold"
                              class="text-danger d-block"
                            >{{ $t('google_ads.display_conditions.pageCounterThreshold_error') }}</small>
                            <small class="text-muted">{{ $t('google_ads.display_conditions.pageCounterThreshold_help') }}</small>
                          </b-form-group>
                          <b-row>
                            <b-col
                              cols="12"
                              md="6"
                            >
                              <b-form-group :label="$t('google_ads.frequency_capping')">
                                <b-form-input
                                  v-model.number="googleAd.config.frequencyCappingHours"
                                  type="number"
                                  min="0"
                                  placeholder="2"
                                />
                                <small class="text-muted">{{ $t("google_ads.frequency_capping_help") }}</small>
                              </b-form-group>
                            </b-col>
                            <b-col
                              cols="12"
                              md="6"
                            >
                              <b-form-group :label="$t('google_ads.show_delay')">
                                <b-form-input
                                  v-model.number="googleAd.config.showDelay"
                                  type="number"
                                  min="0"
                                  placeholder="2000"
                                />
                                <small class="text-muted">{{ $t("google_ads.show_delay_help") }}</small>
                              </b-form-group>
                            </b-col>
                          </b-row>
                        </b-card>

                        <h5 class="mb-2">
                          {{ $t('google_ads.window_behavior') }}
                        </h5>

                        <b-row>
                          <b-col
                            cols="12"
                            md="6"
                          >
                            <b-form-group :label="$t('google_ads.timer_duration')">
                              <b-form-input
                                v-model.number="googleAd.config.timerDuration"
                                type="number"
                                min="0"
                                placeholder="5"
                              />
                              <small class="text-muted">{{ $t("google_ads.timer_duration_help") }}</small>
                            </b-form-group>
                          </b-col>
                          <b-col
                            cols="12"
                            md="6"
                          >
                            <b-form-group :label="$t('google_ads.logo_url')">
                              <b-form-input
                                v-model="googleAd.config.logo"
                                placeholder="https://example.com/logo.png"
                              />
                              <small class="text-muted">{{ $t("google_ads.logo_url_help") }}</small>
                            </b-form-group>
                          </b-col>
                        </b-row>

                        <b-form-group>
                          <b-form-checkbox v-model="googleAd.config.hideIfNoAd">
                            {{ $t("google_ads.hide_if_no_ad") }}
                          </b-form-checkbox>
                        </b-form-group>

                        <b-form-group>
                          <b-form-checkbox v-model="googleAd.config.enableImpressions">
                            {{ $t('google_ads.enable_impressions') }}
                          </b-form-checkbox>
                        </b-form-group>
                      </b-tab>

                      <b-tab ref="modalGeoTab">
                        <template #title>
                          <feather-icon
                            icon="TargetIcon"
                            size="18"
                            class="mr-50"
                          />
                          <span class="font-weight-bold">{{ $t('banners.targetings.title') }}</span>
                        </template>
                        <offer-wall-geo-targeting
                          :targetings="googleAd.config.targetings"
                          :errors="geoErrors"
                        />
                      </b-tab>

                      <!-- Tab B: Texts and Labels -->
                      <b-tab>
                        <template #title>
                          <feather-icon
                            icon="FileTextIcon"
                            size="18"
                            class="mr-50"
                          />
                          <span class="font-weight-bold">{{ $t("google_ads.tabs.texts") }}</span>
                        </template>

                        <p class="text-muted mt-2">
                          {{ $t("google_ads.texts_labels_help") }}
                        </p>

                        <b-row>
                          <!-- Column 1: Title, Message, Timer Text -->
                          <b-col
                            cols="12"
                            md="6"
                          >
                            <b-form-group :label="$t('google_ads.text_title')">
                              <b-form-input
                                v-model="googleAd.config.texts.title"
                                placeholder="Уважаемый читатель"
                              />
                            </b-form-group>

                            <b-form-group
                              :label="$t('google_ads.text_message')"
                              :description="$t('google_ads.text_message_help')"
                            >
                              <b-form-textarea
                                v-model="googleAd.config.texts.message"
                                placeholder="Пожалуйста, посмотрите короткую рекламу,<br> чтобы поддержать сайт."
                                rows="3"
                              />
                            </b-form-group>

                            <b-form-group
                              :label="$t('google_ads.text_timer')"
                              :description="$t('google_ads.text_timer_help')"
                            >
                              <b-form-input
                                v-model="googleAd.config.texts.timerText"
                                placeholder="Вознаграждение через {seconds} с"
                              />
                            </b-form-group>
                          </b-col>

                          <!-- Column 2: Loading Text, Please Wait, Continue Button, Back Button -->
                          <b-col
                            cols="12"
                            md="6"
                          >
                            <b-form-group :label="$t('google_ads.text_ad_loading')">
                              <b-form-input
                                v-model="googleAd.config.texts.adLoading"
                                placeholder="Загрузка рекламы..."
                              />
                            </b-form-group>

                            <b-form-group :label="$t('google_ads.text_please_wait')">
                              <b-form-input
                                v-model="googleAd.config.texts.pleaseWait"
                                placeholder="Пожалуйста, подождите"
                              />
                            </b-form-group>

                            <b-form-group :label="$t('google_ads.text_continue_btn')">
                              <b-form-input
                                v-model="googleAd.config.texts.continueBtn"
                                placeholder="Продолжить"
                              />
                            </b-form-group>

                            <b-form-group :label="$t('google_ads.text_back_btn')">
                              <b-form-input
                                v-model="googleAd.config.texts.backBtn"
                                placeholder="Назад"
                              />
                            </b-form-group>
                          </b-col>
                        </b-row>

                        <h6 class="font-weight-bold mt-2 mb-1">
                          {{ $t('google_ads.texts_options_title') }}
                        </h6>
                        <div class="d-flex flex-wrap align-items-center mb-2">
                          <b-button
                            v-for="index in googleAd.config.textsOptions.length"
                            :key="index"
                            type="button"
                            size="sm"
                            :variant="activeTextsOptionIndex === index - 1 ? 'primary' : 'outline-primary'"
                            class="mr-1 mb-1"
                            @click="activeTextsOptionIndex = index - 1"
                          >
                            {{ $t('google_ads.texts_options_variant', { number: index }) }}
                          </b-button>
                          <b-button
                            type="button"
                            size="sm"
                            variant="outline-primary"
                            class="mb-1"
                            :aria-label="$t('google_ads.texts_options_add')"
                            @click="addTextsOption"
                          >
                            <feather-icon icon="PlusIcon" />
                          </b-button>
                        </div>
                        <b-card
                          v-if="activeTextOption"
                          border-variant="light"
                          class="bg-light mb-2"
                        >
                          <div class="d-flex justify-content-between align-items-center mb-2">
                            <h6 class="font-weight-bold mb-0">
                              {{ $t('google_ads.texts_options_variant', { number: activeTextsOptionIndex + 1 }) }}
                            </h6>
                            <b-button
                              type="button"
                              size="sm"
                              variant="flat-danger"
                              :aria-label="$t('google_ads.texts_options_remove')"
                              @click="removeTextsOption(activeTextsOptionIndex)"
                            >
                              <feather-icon
                                icon="Trash2Icon"
                                class="mr-50"
                              />
                              {{ $t('google_ads.texts_options_remove') }}
                            </b-button>
                          </div>
                          <b-row>
                            <b-col
                              cols="12"
                              md="6"
                            >
                              <b-form-group :label="$t('google_ads.text_title')">
                                <b-form-input v-model="activeTextOption.title" />
                              </b-form-group>
                              <b-form-group
                                :label="$t('google_ads.text_message')"
                                :description="$t('google_ads.text_message_help')"
                              >
                                <b-form-textarea
                                  v-model="activeTextOption.message"
                                  rows="3"
                                />
                              </b-form-group>
                              <b-form-group
                                :label="$t('google_ads.text_timer')"
                                :description="$t('google_ads.text_timer_help')"
                              >
                                <b-form-input v-model="activeTextOption.timerText" />
                              </b-form-group>
                            </b-col>
                            <b-col
                              cols="12"
                              md="6"
                            >
                              <b-form-group :label="$t('google_ads.text_ad_loading')">
                                <b-form-input v-model="activeTextOption.adLoading" />
                              </b-form-group>
                              <b-form-group :label="$t('google_ads.text_please_wait')">
                                <b-form-input v-model="activeTextOption.pleaseWait" />
                              </b-form-group>
                              <b-form-group :label="$t('google_ads.text_continue_btn')">
                                <b-form-input v-model="activeTextOption.continueBtn" />
                              </b-form-group>
                              <b-form-group :label="$t('google_ads.text_back_btn')">
                                <b-form-input v-model="activeTextOption.backBtn" />
                              </b-form-group>
                            </b-col>
                          </b-row>
                        </b-card>
                      </b-tab>

                      <!-- Tab C: Styles -->
                      <b-tab ref="modalStylesTab">
                        <template #title>
                          <feather-icon
                            icon="SlidersIcon"
                            size="18"
                            class="mr-50"
                          />
                          <span class="font-weight-bold">{{ $t("google_ads.tabs.styles") }}</span>
                        </template>

                        <p class="text-muted mt-2">
                          {{ $t("google_ads.styles_section_help") }}
                        </p>

                        <modal-window-size :size="googleAd.config.styles.modal" />

                        <b-row>
                          <b-col
                            cols="12"
                            class="mb-3"
                          >
                            <b-card
                              border-variant="light"
                              class="mb-0 bg-light"
                            >
                              <h6 class="font-weight-bold mb-2">
                                {{ $t('google_ads.blur_offset') }}
                              </h6>
                              <b-row>
                                <b-col
                                  cols="12"
                                  sm="4"
                                  md="3"
                                >
                                  <b-form-input
                                    v-model.number="googleAd.config.blurOffset"
                                    :aria-label="$t('google_ads.blur_offset')"
                                    type="number"
                                    min="0"
                                  />
                                </b-col>
                              </b-row>
                            </b-card>
                          </b-col>
                        </b-row>

                        <b-row v-if="googleAd.config && googleAd.config.styles">
                          <!-- Row 1: Title (Заголовок) -->
                          <b-col
                            cols="12"
                            class="mb-3"
                          >
                            <b-card
                              border-variant="light"
                              class="mb-0 bg-light"
                            >
                              <h6 class="font-weight-bold mb-2">
                                {{ $t('google_ads.style_title') }}
                              </h6>
                              <b-row>
                                <b-col
                                  cols="12"
                                  sm="4"
                                  md="3"
                                >
                                  <b-form-group :label="$t('google_ads.style_color')">
                                    <b-form-input
                                      type="color"
                                      :value="googleAd.config.styles.title.color"
                                      class="custom-color-picker"
                                      @change="val => updateColor('title', val)"
                                    />
                                  </b-form-group>
                                </b-col>
                                <b-col
                                  cols="12"
                                  sm="8"
                                  md="9"
                                >
                                  <b-form-group :label="`${$t('google_ads.style_font_size')} (px)`">
                                    <div
                                      class="d-flex align-items-center"
                                      style="gap: 10px;"
                                    >
                                      <!-- Desktop -->
                                      <b-input-group style="max-width: 100px;">
                                        <b-input-group-prepend
                                          v-b-tooltip.hover.top="$t('google_ads.desktop')"
                                          is-text
                                        >
                                          <feather-icon
                                            icon="MonitorIcon"
                                            size="14"
                                          />
                                        </b-input-group-prepend>
                                        <b-form-input
                                          type="number"
                                          :value="parsePx(googleAd.config.styles.title.fontSize[0])"
                                          class="text-center px-25"
                                          @input="val => updateFontSize(googleAd.config.styles.title.fontSize, 0, val)"
                                        />
                                      </b-input-group>
                                      <!-- Mobile -->
                                      <b-input-group style="max-width: 100px;">
                                        <b-input-group-prepend
                                          v-b-tooltip.hover.top="$t('google_ads.mobile')"
                                          is-text
                                        >
                                          <feather-icon
                                            icon="SmartphoneIcon"
                                            size="14"
                                          />
                                        </b-input-group-prepend>
                                        <b-form-input
                                          type="number"
                                          :value="parsePx(googleAd.config.styles.title.fontSize[1])"
                                          class="text-center px-25"
                                          @input="val => updateFontSize(googleAd.config.styles.title.fontSize, 1, val)"
                                        />
                                      </b-input-group>
                                    </div>
                                  </b-form-group>
                                </b-col>
                              </b-row>
                            </b-card>
                          </b-col>

                          <!-- Row 2: Message (Сообщение) -->
                          <b-col
                            cols="12"
                            class="mb-3"
                          >
                            <b-card
                              border-variant="light"
                              class="mb-0 bg-light"
                            >
                              <h6 class="font-weight-bold mb-2">
                                {{ $t('google_ads.style_message') }}
                              </h6>
                              <b-row>
                                <b-col
                                  cols="12"
                                  sm="4"
                                  md="3"
                                >
                                  <b-form-group :label="$t('google_ads.style_color')">
                                    <b-form-input
                                      type="color"
                                      :value="googleAd.config.styles.message.color"
                                      class="custom-color-picker"
                                      @change="val => updateColor('message', val)"
                                    />
                                  </b-form-group>
                                </b-col>
                                <b-col
                                  cols="12"
                                  sm="8"
                                  md="9"
                                >
                                  <b-form-group :label="`${$t('google_ads.style_font_size')} (px)`">
                                    <div
                                      class="d-flex align-items-center"
                                      style="gap: 10px;"
                                    >
                                      <!-- Desktop -->
                                      <b-input-group style="max-width: 100px;">
                                        <b-input-group-prepend
                                          v-b-tooltip.hover.top="$t('google_ads.desktop')"
                                          is-text
                                        >
                                          <feather-icon
                                            icon="MonitorIcon"
                                            size="14"
                                          />
                                        </b-input-group-prepend>
                                        <b-form-input
                                          type="number"
                                          :value="parsePx(googleAd.config.styles.message.fontSize[0])"
                                          class="text-center px-25"
                                          @input="val => updateFontSize(googleAd.config.styles.message.fontSize, 0, val)"
                                        />
                                      </b-input-group>
                                      <!-- Mobile -->
                                      <b-input-group style="max-width: 100px;">
                                        <b-input-group-prepend
                                          v-b-tooltip.hover.top="$t('google_ads.mobile')"
                                          is-text
                                        >
                                          <feather-icon
                                            icon="SmartphoneIcon"
                                            size="14"
                                          />
                                        </b-input-group-prepend>
                                        <b-form-input
                                          type="number"
                                          :value="parsePx(googleAd.config.styles.message.fontSize[1])"
                                          class="text-center px-25"
                                          @input="val => updateFontSize(googleAd.config.styles.message.fontSize, 1, val)"
                                        />
                                      </b-input-group>
                                    </div>
                                  </b-form-group>
                                </b-col>
                              </b-row>
                            </b-card>
                          </b-col>

                          <!-- Row 3: Timer (Таймер) -->
                          <b-col
                            cols="12"
                            class="mb-3"
                          >
                            <b-card
                              border-variant="light"
                              class="mb-0 bg-light"
                            >
                              <h6 class="font-weight-bold mb-2">
                                {{ $t('google_ads.style_timer') }}
                              </h6>
                              <b-row>
                                <b-col
                                  cols="12"
                                  sm="4"
                                  md="3"
                                >
                                  <b-form-group :label="$t('google_ads.style_color')">
                                    <b-form-input
                                      type="color"
                                      :value="googleAd.config.styles.timer.color"
                                      class="custom-color-picker"
                                      @change="val => updateColor('timer', val)"
                                    />
                                  </b-form-group>
                                </b-col>
                                <b-col
                                  cols="12"
                                  sm="8"
                                  md="9"
                                >
                                  <b-form-group :label="`${$t('google_ads.style_font_size')} (px)`">
                                    <div
                                      class="d-flex align-items-center"
                                      style="gap: 10px;"
                                    >
                                      <!-- Desktop -->
                                      <b-input-group style="max-width: 100px;">
                                        <b-input-group-prepend
                                          v-b-tooltip.hover.top="$t('google_ads.desktop')"
                                          is-text
                                        >
                                          <feather-icon
                                            icon="MonitorIcon"
                                            size="14"
                                          />
                                        </b-input-group-prepend>
                                        <b-form-input
                                          type="number"
                                          :value="parsePx(googleAd.config.styles.timer.fontSize[0])"
                                          class="text-center px-25"
                                          @input="val => updateFontSize(googleAd.config.styles.timer.fontSize, 0, val)"
                                        />
                                      </b-input-group>
                                      <!-- Mobile -->
                                      <b-input-group style="max-width: 100px;">
                                        <b-input-group-prepend
                                          v-b-tooltip.hover.top="$t('google_ads.mobile')"
                                          is-text
                                        >
                                          <feather-icon
                                            icon="SmartphoneIcon"
                                            size="14"
                                          />
                                        </b-input-group-prepend>
                                        <b-form-input
                                          type="number"
                                          :value="parsePx(googleAd.config.styles.timer.fontSize[1])"
                                          class="text-center px-25"
                                          @input="val => updateFontSize(googleAd.config.styles.timer.fontSize, 1, val)"
                                        />
                                      </b-input-group>
                                    </div>
                                  </b-form-group>
                                </b-col>
                              </b-row>
                            </b-card>
                          </b-col>

                          <!-- Row 4: Button (Кнопка) -->
                          <b-col
                            cols="12"
                            class="mb-3"
                          >
                            <b-card
                              border-variant="light"
                              class="mb-0 bg-light"
                            >
                              <h6 class="font-weight-bold mb-2">
                                {{ $t('google_ads.style_button') }}
                              </h6>
                              <b-row>
                                <b-col
                                  cols="4"
                                  sm="4"
                                  md="4"
                                >
                                  <b-form-group :label="$t('google_ads.style_text_color')">
                                    <b-form-input
                                      type="color"
                                      :value="googleAd.config.styles.button.color"
                                      class="custom-color-picker"
                                      @change="val => updateColor('button', val)"
                                    />
                                  </b-form-group>
                                </b-col>
                                <b-col
                                  cols="4"
                                  sm="4"
                                  md="4"
                                >
                                  <b-form-group :label="$t('google_ads.style_bg_color')">
                                    <b-form-input
                                      type="color"
                                      :value="googleAd.config.styles.button.backgroundColor"
                                      class="custom-color-picker"
                                      @change="val => updateButtonBg(val)"
                                    />
                                  </b-form-group>
                                </b-col>
                                <b-col
                                  cols="4"
                                  sm="4"
                                  md="4"
                                >
                                  <b-form-group :label="$t('google_ads.style_hover_bg_color')">
                                    <b-form-input
                                      type="color"
                                      :value="googleAd.config.styles.button.hoverBackgroundColor"
                                      class="custom-color-picker"
                                      @change="val => updateButtonHoverBg(val)"
                                    />
                                  </b-form-group>
                                </b-col>
                                <b-col
                                  cols="12"
                                >
                                  <b-form-group :label="`${$t('google_ads.style_font_size')} (px)`">
                                    <div
                                      class="d-flex align-items-center justify-content-start"
                                      style="gap: 10px;"
                                    >
                                      <!-- Desktop -->
                                      <b-input-group style="max-width: 100px;">
                                        <b-input-group-prepend
                                          v-b-tooltip.hover.top="$t('google_ads.desktop')"
                                          is-text
                                        >
                                          <feather-icon
                                            icon="MonitorIcon"
                                            size="14"
                                          />
                                        </b-input-group-prepend>
                                        <b-form-input
                                          type="number"
                                          :value="parsePx(googleAd.config.styles.button.fontSize[0])"
                                          class="text-center px-25"
                                          @input="val => updateFontSize(googleAd.config.styles.button.fontSize, 0, val)"
                                        />
                                      </b-input-group>
                                      <!-- Mobile -->
                                      <b-input-group style="max-width: 100px;">
                                        <b-input-group-prepend
                                          v-b-tooltip.hover.top="$t('google_ads.mobile')"
                                          is-text
                                        >
                                          <feather-icon
                                            icon="SmartphoneIcon"
                                            size="14"
                                          />
                                        </b-input-group-prepend>
                                        <b-form-input
                                          type="number"
                                          :value="parsePx(googleAd.config.styles.button.fontSize[1])"
                                          class="text-center px-25"
                                          @input="val => updateFontSize(googleAd.config.styles.button.fontSize, 1, val)"
                                        />
                                      </b-input-group>
                                    </div>
                                  </b-form-group>
                                </b-col>
                              </b-row>
                            </b-card>
                          </b-col>
                        </b-row>
                      </b-tab>
                    </b-tabs>
                  </b-tab>

                  <!-- Tab B: In-Image Settings -->
                  <b-tab
                    v-if="googleAd.config.enableInImage"
                    :active="!googleAd.config.enableModal"
                  >
                    <template #title>
                      <feather-icon
                        icon="ImageIcon"
                        size="18"
                        class="mr-50"
                      />
                      <span class="font-weight-bold">{{ $t("google_ads.format_in_image") || "In-Image" }}</span>
                    </template>

                    <b-card
                      border-variant="light"
                      class="mb-0 bg-light mt-2"
                    >
                      <b-row>
                        <!-- Max Overlap -->
                        <b-col
                          cols="12"
                          md="6"
                        >
                          <b-form-group :label="$t('google_ads.in_image_max_overlap') || 'Макс. перекрытие (%)'">
                            <b-form-input
                              v-model.number="googleAd.config.inImage.maxOverlap"
                              type="number"
                              min="0"
                              max="100"
                              placeholder="30"
                            />
                          </b-form-group>
                        </b-col>
                        <b-col
                          cols="12"
                          md="6"
                        >
                          <b-form-group :label="$t('google_ads.frequency_capping')">
                            <b-form-input
                              v-model.number="googleAd.config.inImage.frequencyCappingHours"
                              type="number"
                              min="0"
                              placeholder="2"
                            />
                            <small class="text-muted">{{ $t("google_ads.frequency_capping_help") }}</small>
                          </b-form-group>
                        </b-col>
                      </b-row>

                    </b-card>
                  </b-tab>
                </b-tabs>

                <!-- Alert shown when no format is checked -->
                <b-alert
                  v-else
                  variant="warning"
                  show
                  class="mt-2"
                >
                  <div class="alert-body d-flex align-items-center">
                    <feather-icon
                      icon="AlertTriangleIcon"
                      class="mr-50"
                      size="18"
                    />
                    <span>{{ $t("google_ads.no_formats_enabled") || "Пожалуйста, включите хотя бы один формат рекламы в настройках выше." }}</span>
                  </div>
                </b-alert>
              </b-col>

              <!-- Right Column: Sticky Live Preview at Top Page Level -->
              <b-col
                cols="12"
                lg="5"
                class="mt-3 mt-lg-0"
              >
                <div class="sticky-preview">
                  <!-- Modal Preview container -->
                  <div v-if="currentPreviewFormat === 'modal'">
                    <div class="mb-2">
                      <h5 class="text-muted">
                        {{ $t("google_ads.preview_modal") }}
                      </h5>
                    </div>
                    <div :class="['preview-device-wrapper', stylesDeviceTab === 1 ? 'preview-device-wrapper--mobile' : 'preview-device-wrapper--desktop']">
                      <div
                        class="preview-modal-box p-4 border shadow-sm bg-white"
                        style="border-radius: 12px !important; position: relative;"
                        :style="modalPreviewStyle"
                      >
                        <!-- Absolute positioned timer on the top-right -->
                        <div
                          class="preview-timer font-weight-normal text-right text-muted"
                          :style="timerStyle"
                        >
                          {{ displayTimerText }}
                        </div>

                        <div
                          class="mb-3"
                          style="min-height: 80px; display: flex; align-items: center; justify-content: center;"
                        >
                          <img
                            v-if="googleAd.config.logo"
                            :src="googleAd.config.logo"
                            style="max-height: 80px; max-width: 100%; object-fit: contain;"
                            alt="Logo"
                          >
                          <div
                            v-else
                            class="text-muted"
                            style="font-style: italic; font-size: 0.9rem;"
                          >
                            Логотип
                          </div>
                        </div>

                        <h4
                          class="font-weight-bold text-dark mb-2 text-center"
                          :style="titleStyle"
                        >
                          {{ googleAd.config.texts.title || 'Уважаемый читатель' }}
                        </h4>

                        <!-- eslint-disable vue/no-v-html -->
                        <p
                          class="text-secondary mb-3 text-center"
                          style="line-height: 1.5;"
                          :style="messageStyle"
                          v-html="googleAd.config.texts.message || 'Пожалуйста, посмотрите короткую рекламу...  '"
                        />
                        <!-- eslint-enable vue/no-v-html -->

                        <b-button
                          block
                          class="mt-3 font-weight-bold py-2"
                          :style="btnStyle"
                          @mouseenter="btnHovered = true"
                          @mouseleave="btnHovered = false"
                        >
                          {{ googleAd.config.texts.continueBtn || 'Продолжить' }}
                        </b-button>
                      </div>
                    </div>

                    <!-- Device view switcher at the bottom -->
                    <div class="d-flex justify-content-center mt-2">
                      <b-form-radio-group
                        v-model="stylesDeviceTab"
                        buttons
                        button-variant="outline-primary"
                        size="sm"
                      >
                        <b-form-radio :value="0">
                          <feather-icon
                            icon="MonitorIcon"
                            class="mr-25"
                          />
                          {{ $t('google_ads.desktop') }}
                        </b-form-radio>
                        <b-form-radio :value="1">
                          <feather-icon
                            icon="SmartphoneIcon"
                            class="mr-25"
                          />
                          {{ $t('google_ads.mobile') }}
                        </b-form-radio>
                      </b-form-radio-group>
                    </div>
                  </div>

                  <!-- In-Image Preview container -->
                  <div v-if="currentPreviewFormat === 'in-image'">
                    <div class="mb-2">
                      <h5 class="text-muted">
                        {{ $t("google_ads.preview_in_image") || "Предпросмотр рекламы на изображении" }}
                      </h5>
                    </div>
                    <div
                      class="preview-device-wrapper preview-device-wrapper--desktop p-2 bg-light border rounded"
                      style="min-height: auto;"
                    >
                      <div
                        class="preview-article-box p-3 border shadow-sm bg-white rounded w-100"
                        style="font-family: 'Inter', sans-serif;"
                      >
                        <!-- Simulated Article Title -->
                        <div
                          class="mb-2"
                          style="height: 16px; width: 80%; background-color: #e9ecef; border-radius: 4px;"
                        />

                        <!-- Simulated Article Text lines -->
                        <div
                          class="mb-3 d-flex flex-column"
                          style="gap: 6px;"
                        >
                          <div style="height: 8px; width: 100%; background-color: #f1f3f5; border-radius: 2px;" />
                          <div style="height: 8px; width: 95%; background-color: #f1f3f5; border-radius: 2px;" />
                        </div>

                        <!-- Mock Image Container with Ad Overlay -->
                        <div
                          class="position-relative w-100 overflow-hidden rounded bg-secondary"
                          style="height: 240px;"
                        >
                          <img
                            src="https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=600&amp;q=80"
                            style="object-fit: cover; height: 100%; width: 100%; display: block;"
                            alt="Landscape"
                          >
                          <!-- Ad Overlay positioned dynamically based on config -->
                          <div
                            :class="['mock-in-image-ad', googleAd.config.inImage.position || 'bottom-right']"
                            style="position: absolute; background: rgba(255, 255, 255, 0.95); border: 1px solid #ced4da; box-shadow: 0 4px 12px rgba(0,0,0,0.15); border-radius: 6px; padding: 8px; display: flex; flex-direction: column; width: 150px; height: 100px; transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1); z-index: 5;"
                          >
                            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; width: 100%;">
                              <span style="font-size: 9px; font-weight: bold; background: #e9ecef; border: 1px solid #ced4da; padding: 1px 4px; border-radius: 2px; color: #495057; line-height: 1;">Ad</span>
                              <span style="font-size: 14px; color: #adb5bd; cursor: pointer; line-height: 1;">&times;</span>
                            </div>
                            <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; flex-grow: 1; text-align: center;">
                              <span style="font-size: 11px; font-weight: bold; color: #212529;">Google Ads</span>
                              <span style="font-size: 9px; color: #6c757d; margin-top: 2px;">{{ getInImagePreviewSizeText }}</span>
                            </div>
                          </div>
                        </div>

                        <!-- Simulated Article Text lines after image -->
                        <div
                          class="mt-3 d-flex flex-column"
                          style="gap: 6px;"
                        >
                          <div style="height: 8px; width: 100%; background-color: #f1f3f5; border-radius: 2px;" />
                          <div style="height: 8px; width: 90%; background-color: #f1f3f5; border-radius: 2px;" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </b-col>
            </b-row>

            <!-- Save Button -->
            <b-row class="mt-3">
              <b-col
                cols="12"
                class="d-flex align-items-center justify-content-end"
              >
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="outline-secondary"
                  class="mr-0 mr-sm-1"
                  @click="$router.go(-1)"
                >
                  <feather-icon
                    icon="ChevronLeftIcon"
                    class="mr-50"
                  />
                  <span class="align-middle">{{ $t("form.back") }}</span>
                </b-button>
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  :disabled="disabledBtn"
                  @click="saveForm"
                >
                  <span class="align-middle">{{ $t("form.save") }}</span>
                  <feather-icon
                    icon="ChevronRightIcon"
                    class="ml-50"
                  />
                </b-button>
              </b-col>
            </b-row>
          </b-card>
        </b-tab>

        <!-- Tab 2: Ad Platform Settings (admin only) -->
        <b-tab v-if="isAdmin">
          <template #title>
            <feather-icon
              icon="LayersIcon"
              size="18"
              class="mr-50"
            />
            <span class="font-weight-bold">{{ $t("google_ads.ad_platform_settings") }}</span>
          </template>
          <b-card>
            <div class="head-card">
              <div class="head-card-top">
                <h4>{{ $t("google_ads.ad_platform_settings") }}</h4>
              </div>
              <hr>
            </div>

            <b-form-group
              v-if="isDashboardAdmin"
              :label="$t('google_ads.provider_id')"
              label-for="i-provider-id"
            >
              <b-form-input
                id="i-provider-id"
                v-model="googleAd.provider_id"
              />
            </b-form-group>
            <b-form-group
              v-if="isDashboardAdmin"
              :label="$t('google_ads.partner_rate')"
              label-for="i-partner-rate"
            >
              <b-input-group class="input-group-merge">
                <b-input-group-prepend is-text>
                  <feather-icon icon="PercentIcon" />
                </b-input-group-prepend>
                <b-form-input
                  id="i-partner-rate"
                  v-model="googleAd.partner_rate"
                  type="number"
                  step="0.01"
                  min="0"
                />
              </b-input-group>
            </b-form-group>
            <!-- Phoenix Rate -->
            <b-form-group
              v-if="isDashboardAdmin"
              :label="$t('partners.phx_rate')"
              label-for="i-phx_rate"
            >
              <b-input-group class="input-group-merge">
                <b-input-group-prepend is-text>
                  <feather-icon icon="PercentIcon" />
                </b-input-group-prepend>
                <b-form-input
                  id="i-phx_rate"
                  v-model="googleAd.phoenix_rate"
                  placeholder="10"
                  autocomplete="phoenix_rate"
                  type="number"
                  step="0.01"
                  min="0"
                />
              </b-input-group>
            </b-form-group>
            <!-- Ad Type (Select dropdown) -->
            <b-form-group
              v-if="isAdmin"
              :label="$t('google_ads.ad_type')"
            >
              <b-form-select
                v-model="googleAd.config.adType"
                :options="adTypeOptions"
              />
            </b-form-group>

            <div
              v-if="googleAd.config.adType === 'adsense'"
              class="mt-2"
            >
              <b-alert
                variant="info"
                show
              >
                <div class="alert-body">
                  <span>
                    <feather-icon
                      icon="InfoIcon"
                      class="mr-50"
                    />
                    {{ $t("google_ads.adsense_optional") }}
                  </span>
                </div>
              </b-alert>
              <b-row>
                <b-col
                  cols="12"
                  md="6"
                >
                  <b-form-group :label="$t('google_ads.adsense_ad_client')">
                    <b-form-input
                      v-model="googleAd.config.adsense.adClient"
                      placeholder="ca-pub-XXXXXXXX"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  cols="12"
                  md="6"
                >
                  <b-form-group :label="$t('google_ads.adsense_ad_slot')">
                    <b-form-input
                      v-model="googleAd.config.adsense.adSlot"
                      placeholder="XXXXXXXXXX"
                    />
                  </b-form-group>
                </b-col>
              </b-row>

              <b-form-group :label="$t('google_ads.adsense_ad_size')">
                <b-row>
                  <b-col cols="6">
                    <b-form-input
                      v-model.number="googleAd.config.adsense.adSize[0]"
                      type="number"
                      :placeholder="$t('google_ads.width_placeholder')"
                    />
                  </b-col>
                  <b-col cols="6">
                    <b-form-input
                      v-model.number="googleAd.config.adsense.adSize[1]"
                      type="number"
                      :placeholder="$t('google_ads.height_placeholder')"
                    />
                  </b-col>
                </b-row>
              </b-form-group>
            </div>

            <div v-else>
              <b-form-group
                class="mt-2"
                :label="$t('google_ads.site_domain')"
              >
                <div class="site-domain-control">
                  <b-input-group class="input-group-merge">
                    <b-input-group-prepend is-text>
                      <feather-icon icon="GlobeIcon" />
                    </b-input-group-prepend>
                    <b-form-input
                      v-model="googleAd.config.gpt.siteDomain"
                      placeholder="example.com"
                      autocomplete="off"
                      type="text"
                    />
                  </b-input-group>
                  <SiteDomainPicker
                    v-if="isDashboardAdmin"
                    v-model="googleAd.config.gpt.siteDomain"
                    :widget-id="googleAd._id || ''"
                    :has-ad-unit-path="hasSiteDomainAdUnitPath"
                  />
                </div>
              </b-form-group>

              <!-- Main Format tabs for Ad Platform Settings -->
              <b-tabs
                v-model="activePlatformFormatTab"
                pills
                class="mt-2"
                nav-class="mb-2"
              >
                <!-- Tab A: Modal Window (Модальное окно) Settings -->
                <b-tab v-if="googleAd.config.enableModal">
                  <template #title>
                    <feather-icon
                      icon="Maximize2Icon"
                      size="18"
                      class="mr-50"
                    />
                    <span class="font-weight-bold">{{ $t("google_ads.format_modal") || "Модальное окно" }}</span>
                  </template>

                  <b-tabs
                    v-model="activeAdTab"
                    content-class="mt-2"
                    nav-class="d-none"
                  >
                    <!-- Google Ad Manager (GPT) Tab -->
                    <b-tab
                      :title="$t('google_ads.gpt_title')"
                      active
                    >
                      <b-form-group :label="$t('google_ads.gpt_ad_unit_path_rewarded')">
                        <validation-provider
                          #default="{ errors }"
                          :name="$t('google_ads.gpt_ad_unit_path_rewarded')"
                          rules="required"
                        >
                          <b-form-input
                            v-model="googleAd.config.gpt.adUnitPathRewarded"
                            :state="errors.length > 0 ? false : null"
                            placeholder="/22247219933,227399588/Chordzaa_Adx/phoenixrewarded"
                          />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                      </b-form-group>

                      <b-form-group class="mb-1">
                        <b-form-checkbox v-model="googleAd.config.enableFallback">
                          {{ $t('google_ads.enable_fallback') }}
                        </b-form-checkbox>
                        <small class="d-block text-muted mt-50">
                          {{ $t('google_ads.enable_fallback_help') }}
                        </small>
                      </b-form-group>

                      <b-form-group :label="$t('google_ads.gpt_fallback_ad_unit_path')">
                        <validation-provider
                          #default="{ errors }"
                          :name="$t('google_ads.gpt_fallback_ad_unit_path')"
                          :rules="googleAd.config.enableFallback ? 'required' : ''"
                        >
                          <b-form-input
                            v-model="googleAd.config.gpt.adUnitPath"
                            :disabled="!googleAd.config.enableFallback"
                            :state="errors.length > 0 ? false : null"
                            placeholder="/22247219933,227399588/Chordzaa_Adx/phoenixinterstitial"
                          />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                      </b-form-group>

                      <b-form-group :label="$t('google_ads.content_selector')">
                        <b-input-group class="input-group-merge">
                          <b-input-group-prepend is-text>
                            <feather-icon icon="FileTextIcon" />
                          </b-input-group-prepend>
                          <b-form-input
                            v-model="googleAd.config.gpt.contentSelector"
                            placeholder=".content-area, article"
                            autocomplete="off"
                            type="text"
                          />
                        </b-input-group>
                      </b-form-group>

                      <b-form-group
                        v-if="isAdmin"
                        :label="$t('google_ads.gpt_extract_sizes_script_label')"
                      >
                        <b-form-textarea
                          v-model="adSizesScriptInput"
                          rows="5"
                          :placeholder="$t('google_ads.gpt_extract_sizes_script_placeholder')"
                        />
                        <div class="d-flex align-items-center mt-1">
                          <b-button
                            variant="outline-success"
                            size="sm"
                            :disabled="!adSizesScriptInput || !adSizesScriptInput.trim()"
                            @click="fillAdSizesFromScript"
                          >
                            <feather-icon
                              icon="DownloadIcon"
                              class="mr-25"
                            />
                            {{ $t("google_ads.gpt_extract_sizes_btn") }}
                          </b-button>
                        </div>
                        <small class="d-block text-muted mt-1">{{ $t("google_ads.gpt_extract_sizes_help") }}</small>
                      </b-form-group>

                      <b-form-group :label="$t('google_ads.gpt_ad_sizes')">
                        <div
                          v-for="(size, idx) in googleAd.config.gpt.adSize"
                          :key="`size-${idx}`"
                          class="mb-2"
                        >
                          <b-row>
                            <b-col cols="10">
                              <b-form-input
                                v-if="typeof size === 'string'"
                                v-model="googleAd.config.gpt.adSize[idx]"
                                placeholder="fluid"
                              />
                              <b-input-group v-else>
                                <b-form-input
                                  v-model.number="googleAd.config.gpt.adSize[idx][0]"
                                  type="number"
                                  placeholder="Width"
                                />
                                <b-input-group-text>x</b-input-group-text>
                                <b-form-input
                                  v-model.number="googleAd.config.gpt.adSize[idx][1]"
                                  type="number"
                                  placeholder="Height"
                                />
                              </b-input-group>
                            </b-col>
                            <b-col cols="2">
                              <b-button
                                variant="danger"
                                size="sm"
                                @click="removeAdSize(idx)"
                              >
                                <feather-icon
                                  icon="XIcon"
                                  size="18"
                                />
                              </b-button>
                            </b-col>
                          </b-row>
                        </div>
                        <b-button
                          variant="outline-primary"
                          size="sm"
                          class="mr-1"
                          @click="addAdSize"
                        >
                          <feather-icon
                            icon="PlusIcon"
                            class="mr-25"
                          />
                          {{ $t("google_ads.gpt_add_size") }}
                        </b-button>
                        <b-button
                          v-if="!hasFluidSize"
                          variant="outline-info"
                          size="sm"
                          @click="addFluidSize"
                        >
                          <feather-icon
                            icon="DropletIcon"
                            class="mr-25"
                          />
                          {{ $t("google_ads.gpt_add_fluid") }}
                        </b-button>
                        <small class="d-block text-muted mt-1">{{ $t("google_ads.gpt_sizes_help") }}</small>
                      </b-form-group>
                    </b-tab>

                    <!-- Google AdSense Tab -->
                    <b-tab :title="$t('google_ads.adsense_title')">
                      <b-alert
                        variant="info"
                        show
                      >
                        <div class="alert-body">
                          <span>
                            <feather-icon
                              icon="InfoIcon"
                              class="mr-50"
                            />
                            {{ $t("google_ads.adsense_optional") }}
                          </span>
                        </div>
                      </b-alert>
                      <b-row>
                        <b-col
                          cols="12"
                          md="6"
                        >
                          <b-form-group :label="$t('google_ads.adsense_ad_client')">
                            <b-form-input
                              v-model="googleAd.config.adsense.adClient"
                              placeholder="ca-pub-XXXXXXXX"
                            />
                          </b-form-group>
                        </b-col>

                        <b-col
                          cols="12"
                          md="6"
                        >
                          <b-form-group :label="$t('google_ads.adsense_ad_slot')">
                            <b-form-input
                              v-model="googleAd.config.adsense.adSlot"
                              placeholder="XXXXXXXXXX"
                            />
                          </b-form-group>
                        </b-col>
                      </b-row>

                      <b-form-group :label="$t('google_ads.adsense_ad_size')">
                        <b-row>
                          <b-col cols="6">
                            <b-form-input
                              v-model.number="googleAd.config.adsense.adSize[0]"
                              type="number"
                              :placeholder="$t('google_ads.width_placeholder')"
                            />
                          </b-col>
                          <b-col cols="6">
                            <b-form-input
                              v-model.number="googleAd.config.adsense.adSize[1]"
                              type="number"
                              :placeholder="$t('google_ads.height_placeholder')"
                            />
                          </b-col>
                        </b-row>
                      </b-form-group>
                    </b-tab>

                  </b-tabs>
                </b-tab>

                <!-- Tab B: In-Image Settings -->
                <b-tab v-if="googleAd.config.enableInImage">
                  <template #title>
                    <feather-icon
                      icon="ImageIcon"
                      size="18"
                      class="mr-50"
                    />
                    <span class="font-weight-bold">{{ $t("google_ads.format_in_image") || "In-Image" }}</span>
                  </template>

                  <div class="mt-2">
                    <!-- In-Image GPT Section (Separate from Modal) -->
                    <div
                      v-if="googleAd.config.enableInImage"
                    >
                      <!-- Slots List -->
                      <h5 class="font-weight-bold">
                        {{ $t("google_ads.in_image_slots_title") }}
                      </h5>
                      <small class="text-muted d-block mb-2">{{ $t("google_ads.in_image_slots_help") }}</small>

                      <div
                        v-for="(slot, idx) in googleAd.config.inImage.slots"
                        :key="`slot-${idx}`"
                        class="mb-2 p-2 border rounded bg-white position-relative"
                      >
                        <b-row class="align-items-center">
                          <b-col
                            cols="12"
                            md="3"
                          >
                            <h6 class="font-weight-bold mb-0">
                              {{ $t('google_ads.in_image_slot_ad_unit_path') }} #{{ idx + 1 }}
                            </h6>
                          </b-col>
                          <b-col
                            cols="10"
                            md="7"
                          >
                            <b-form-group class="mb-0">
                              <b-form-input
                                v-model="googleAd.config.inImage.slots[idx]"
                                placeholder="/22247219933,227399588/Chordzaa_Adx/phoenixinimage"
                              />
                            </b-form-group>
                          </b-col>
                          <b-col
                            cols="2"
                            md="2"
                            class="text-right"
                          >
                            <b-button
                              v-if="googleAd.config.inImage.slots.length > 1"
                              variant="flat-danger"
                              size="sm"
                              class="btn-icon p-0"
                              style="width: 28px; height: 28px;"
                              @click="removeInImageSlot(idx)"
                            >
                              <feather-icon
                                icon="Trash2Icon"
                                size="16"
                              />
                            </b-button>
                          </b-col>
                        </b-row>
                      </div>
                      <b-button
                        v-if="googleAd.config.inImage.slots.length < 3"
                        variant="outline-primary"
                        size="sm"
                        class="mb-2"
                        @click="addInImageSlot"
                      >
                        <feather-icon
                          icon="PlusIcon"
                          class="mr-25"
                        />
                        {{ $t("google_ads.in_image_add_slot") }}
                      </b-button>

                      <b-form-group :label="$t('google_ads.in_image_content_selector') || 'Селектор контентной области'">
                        <b-input-group class="input-group-merge">
                          <b-input-group-prepend is-text>
                            <feather-icon icon="FileTextIcon" />
                          </b-input-group-prepend>
                          <b-form-input
                            v-model="googleAd.config.inImage.contentSelector"
                            placeholder=".content-area, article, .post-content"
                            autocomplete="off"
                            type="text"
                          />
                        </b-input-group>
                        <small class="text-muted">{{ $t("google_ads.in_image_content_selector_help") || "CSS-селектор области, в которой искать изображения" }}</small>
                      </b-form-group>

                      <!-- Extract Sizes Section -->
                      <b-form-group
                        v-if="isAdmin"
                        :label="$t('google_ads.in_image_extract_sizes_label') || 'Извлечь размеры из скрипта'"
                      >
                        <b-form-textarea
                          v-model="inImageAdSizesScriptInput"
                          rows="5"
                          placeholder="Параметры скрипта..."
                        />
                        <div class="d-flex align-items-center mt-1">
                          <b-button
                            variant="outline-success"
                            size="sm"
                            :disabled="!inImageAdSizesScriptInput || !inImageAdSizesScriptInput.trim()"
                            @click="fillInImageAdSizesFromScript"
                          >
                            <feather-icon
                              icon="DownloadIcon"
                              class="mr-25"
                            />
                            {{ $t("google_ads.gpt_extract_sizes_btn") }}
                          </b-button>
                        </div>
                      </b-form-group>

                      <!-- Sizes List -->
                      <b-form-group :label="$t('google_ads.in_image_ad_sizes') || 'Размеры рекламы'">
                        <div
                          v-for="(size, idx) in googleAd.config.inImage.adSize"
                          :key="`in-image-size-${idx}`"
                          class="mb-2"
                        >
                          <b-row>
                            <b-col cols="10">
                              <b-form-input
                                v-if="typeof size === 'string'"
                                v-model="googleAd.config.inImage.adSize[idx]"
                                placeholder="fluid"
                              />
                              <b-input-group v-else>
                                <b-form-input
                                  v-model.number="googleAd.config.inImage.adSize[idx][0]"
                                  type="number"
                                  placeholder="Width"
                                />
                                <b-input-group-text>x</b-input-group-text>
                                <b-form-input
                                  v-model.number="googleAd.config.inImage.adSize[idx][1]"
                                  type="number"
                                  placeholder="Height"
                                />
                              </b-input-group>
                            </b-col>
                            <b-col cols="2">
                              <b-button
                                variant="danger"
                                size="sm"
                                @click="removeInImageAdSize(idx)"
                              >
                                <feather-icon
                                  icon="XIcon"
                                  size="18"
                                />
                              </b-button>
                            </b-col>
                          </b-row>
                        </div>
                        <b-button
                          variant="outline-primary"
                          size="sm"
                          class="mr-1"
                          @click="addInImageAdSize"
                        >
                          <feather-icon
                            icon="PlusIcon"
                            class="mr-25"
                          />
                          {{ $t("google_ads.gpt_add_size") }}
                        </b-button>
                        <b-button
                          v-if="!hasInImageFluidSize"
                          variant="outline-info"
                          size="sm"
                          @click="addInImageFluidSize"
                        >
                          <feather-icon
                            icon="DropletIcon"
                            class="mr-25"
                          />
                          {{ $t("google_ads.gpt_add_fluid") }}
                        </b-button>
                        <small class="d-block text-muted mt-1">{{ $t("google_ads.in_image_sizes_help") }}</small>
                      </b-form-group>
                    </div>
                  </div>
                </b-tab>
              </b-tabs>
            </div>

            <!-- Save Button -->
            <b-row>
              <b-col
                md="12"
                class="d-flex align-items-center justify-content-end mt-1"
              >
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="outline-secondary"
                  class="mr-0 mr-sm-1"
                  @click="$router.go(-1)"
                >
                  <feather-icon
                    icon="ChevronLeftIcon"
                    class="mr-50"
                  />
                  <span class="align-middle">{{ $t("form.back") }}</span>
                </b-button>
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  :disabled="disabledBtn"
                  @click="saveForm"
                >
                  <span class="align-middle">{{ $t("form.save") }}</span>
                  <feather-icon
                    icon="ChevronRightIcon"
                    class="ml-50"
                  />
                </b-button>
              </b-col>
            </b-row>
          </b-card>
        </b-tab>
      </b-tabs>
    </validation-observer>

    <!-- Preview Card (admin only) -->
    <b-card
      v-if="googleAd._id && isAdmin"
      class="mt-2"
    >
      <div
        class="d-flex justify-content-between align-items-center"
        style="cursor: pointer;"
        @click="showConfigPreview = !showConfigPreview"
      >
        <h5 class="mb-0">
          {{ $t("google_ads.config_preview") }}
        </h5>
        <b-button
          variant="flat-secondary"
          size="sm"
          class="btn-icon rounded-circle p-0 mr-1"
          style="width: 32px; height: 32px;"
        >
          <feather-icon
            :icon="showConfigPreview ? 'ChevronUpIcon' : 'ChevronDownIcon'"
            size="18"
          />
        </b-button>
      </div>

      <b-collapse
        id="collapse-config-preview"
        v-model="showConfigPreview"
        class="mt-2"
      >
        <pre class="bg-light p-1 rounded mb-0">{{
          JSON.stringify(googleAd.config, null, 2)
        }}</pre>
      </b-collapse>
    </b-card>
  </div>
</template>

<script>
import { isDashboardAdmin, hasOfferWallOperatorRole } from '@/auth/roles'
import useJwt from '@/auth/jwt/useJwt'
import { getUserData } from '@/auth/utils'
import options from '@/libs/options'
import Ripple from 'vue-ripple-directive'
import { required } from '@validations'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BCard,
  BAlert,
  VBTooltip,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BFormSelect,
  BFormTextarea,
  BButton,
  BRow,
  BCol,
  BInputGroup,
  BInputGroupPrepend,
  BInputGroupText,
  BTabs,
  BTab,
  BFormRadioGroup,
  BFormRadio,
  BCollapse,
} from 'bootstrap-vue'
import {
  formToApi, displayConditionErrors, normalizeGeoTargetings, geoTargetingErrors,
} from './googleAdsTransform'
import SiteDomainPicker from './SiteDomainPicker.vue'
import ModalWindowSize from './ModalWindowSize.vue'
import OfferWallGeoTargeting from './OfferWallGeoTargeting.vue'
import { normalizeModalSize, modalSizeErrors, modalDimensionCss } from './modalWindowSize'

export default {
  components: {
    SiteDomainPicker,
    ModalWindowSize,
    OfferWallGeoTargeting,
    BCard,
    BAlert,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BFormSelect,
    BFormTextarea,
    BButton,
    BRow,
    BCol,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupText,
    BTabs,
    BTab,
    BFormRadioGroup,
    BFormRadio,
    BCollapse,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  props: {
    googleAd: {
      type: Object,
      require: true,
      default: () => {},
    },
    type: {
      type: String,
      default: 'edit',
    },
  },
  data() {
    return {
      required,
      authUserData: getUserData(),
      account: {},
      disabledBtn: false,
      displayConditionsSubmitted: false,
      geoSubmitted: false,
      adSizesScriptInput: '',
      stylesDeviceTab: 0,
      btnHovered: false,
      showConfigPreview: false,
      activeMainTab: 0,
      activeFormatTab: 0,
      activeTextsOptionIndex: null,
      activePlatformFormatTab: 0,
      inImageAdSizesScriptInput: '',
      ...options,
    }
  },
  computed: {
    hasSiteDomainAdUnitPath() {
      const gpt = this.googleAd.config?.gpt || {}
      return Boolean(String(gpt.adUnitPath || '').trim() || String(gpt.adUnitPathRewarded || '').trim())
    },
    geoErrors() {
      return this.geoSubmitted ? geoTargetingErrors(this.googleAd.config?.targetings) : {}
    },
    displayConditionsErrors() {
      return displayConditionErrors(this.googleAd.config)
    },
    activeTextOption() {
      return this.googleAd.config?.textsOptions?.[this.activeTextsOptionIndex] || null
    },
    currentPreviewFormat() {
      if (!this.googleAd.config) return null
      // If only modal is enabled, show modal preview
      if (this.googleAd.config.enableModal && !this.googleAd.config.enableInImage) {
        return 'modal'
      }
      // If only in-image is enabled, show in-image preview
      if (!this.googleAd.config.enableModal && this.googleAd.config.enableInImage) {
        return 'in-image'
      }
      // If both are enabled, look at activeFormatTab index
      if (this.googleAd.config.enableModal && this.googleAd.config.enableInImage) {
        return this.activeFormatTab === 1 ? 'in-image' : 'modal'
      }
      return null
    },
    adTypeOptions() {
      return [
        { value: 'gpt', text: this.$t('google_ads.gpt_title') },
        { value: 'adsense', text: this.$t('google_ads.adsense_title') },
      ]
    },
    statusesLocale() {
      return this.statuses.map(s => ({
        value: s.value,
        text: this.$t(`selects.statuses.${s.value}`),
      }))
    },
    hasFluidSize() {
      return this.googleAd.config?.gpt?.adSize?.includes('fluid') || false
    },
    hasInImageFluidSize() {
      return this.googleAd.config?.inImage?.adSize?.includes('fluid') || false
    },
    getInImagePreviewSizeText() {
      const sizes = this.googleAd.config?.inImage?.adSize || []
      if (!sizes.length) return '300x250'
      const first = sizes[0]
      if (first === 'fluid') return 'Fluid'
      if (Array.isArray(first)) return `${first[0]}x${first[1]}`
      return String(first)
    },
    hasConfiguredInImageSlot() {
      return (this.googleAd.config?.inImage?.slots || [])
        .some(slot => String(slot || '').trim())
    },
    isDashboardAdmin() {
      return isDashboardAdmin(this.authUserData)
    },
    isAdmin() {
      return isDashboardAdmin(this.authUserData) || hasOfferWallOperatorRole(this.authUserData)
    },
    activeAdTab: {
      get() {
        return this.googleAd.config?.adType === 'adsense' ? 1 : 0
      },
      set(val) {
        if (!this.googleAd.config) {
          this.$set(this.googleAd, 'config', {})
        }
        this.$set(this.googleAd.config, 'adType', val === 1 ? 'adsense' : 'gpt')
      },
    },
    modalPreviewStyle() {
      const size = this.googleAd.config?.styles?.modal || {}
      return {
        width: modalDimensionCss(size.width?.[this.stylesDeviceTab]),
        height: modalDimensionCss(size.height?.[this.stylesDeviceTab]),
        maxWidth: '100%',
        maxHeight: this.stylesDeviceTab === 1 ? '540px' : '70vh',
        overflow: 'auto',
      }
    },
    titleStyle() {
      const title = this.googleAd.config?.styles?.title || {}
      const activeTab = this.stylesDeviceTab
      const fontSize = title.fontSize ? title.fontSize[activeTab] : '20px'
      const color = title.color || '#1a1a1a'
      return {
        fontSize,
        color,
      }
    },
    messageStyle() {
      const message = this.googleAd.config?.styles?.message || {}
      const activeTab = this.stylesDeviceTab
      const fontSize = message.fontSize ? message.fontSize[activeTab] : '18px'
      const color = message.color || '#555555'
      return {
        fontSize,
        color,
      }
    },
    timerStyle() {
      const timer = this.googleAd.config?.styles?.timer || {}
      const activeTab = this.stylesDeviceTab
      const fontSize = timer.fontSize ? timer.fontSize[activeTab] : '14px'
      const color = timer.color || '#6c757d'
      return {
        fontSize,
        color,
      }
    },
    btnStyle() {
      const btn = this.googleAd.config?.styles?.button || {}
      const activeTab = this.stylesDeviceTab
      const fontSize = btn.fontSize ? btn.fontSize[activeTab] : '16px'
      const color = btn.color || '#ffffff'
      const bgColor = this.btnHovered
        ? (btn.hoverBackgroundColor || '#1565c0')
        : (btn.backgroundColor || '#1976d2')
      return {
        fontSize,
        color: `${color} !important`,
        backgroundColor: `${bgColor} !important`,
        borderColor: `${bgColor} !important`,
        borderRadius: '8px',
        transition: 'all 0.2s ease',
      }
    },
    displayTimerText() {
      const text = this.googleAd.config?.texts?.timerText || 'Вознаграждение через {seconds} с'
      const duration = this.googleAd.config?.timerDuration ?? 5
      return text.replace('{seconds}', duration)
    },
  },
  watch: {},
  created() {
    this.getUserData()
    this.initForm()
  },
  methods: {
    initForm() {
      // Initialize default values if not present
      if (!this.googleAd.config) {
        this.$set(this.googleAd, 'config', {})
      }
      if (this.googleAd.config.blurOffset == null) {
        this.$set(this.googleAd.config, 'blurOffset', 0)
      }
      if (!this.googleAd.config.gpt) {
        this.$set(this.googleAd.config, 'gpt', {
          adUnitPath: '',
          adUnitPathRewarded: '',
          adSize: [[300, 250]],
        })
      }
      if (!this.googleAd.config.adsense) {
        this.$set(this.googleAd.config, 'adsense', {
          adClient: null,
          adSlot: null,
          adSize: [300, 250],
        })
      }
      if (!this.googleAd.config.gpt.adSize) {
        this.$set(this.googleAd.config.gpt, 'adSize', [[300, 250]])
      }
      if (this.googleAd.config.gpt.adUnitPathRewarded === undefined) {
        this.$set(this.googleAd.config.gpt, 'adUnitPathRewarded', '')
      }
      if (!this.googleAd.config.adsense.adSize) {
        this.$set(this.googleAd.config.adsense, 'adSize', [300, 250])
      }
      if (!this.googleAd.config.texts) {
        this.$set(this.googleAd.config, 'texts', {
          title: 'Уважаемый читатель',
          message:
            'Пожалуйста, посмотрите короткую рекламу,<br> чтобы поддержать сайт.',
          continueBtn: 'Продолжить',
          backBtn: 'Назад',
          timerText: 'Вознаграждение через {seconds} с',
          adLoading: 'Загрузка рекламы...',
          pleaseWait: 'Пожалуйста, подождите',
        })
      } else {
        // Clean up obsolete keys if they exist
        if (this.googleAd.config.texts.supportTitle !== undefined) {
          delete this.googleAd.config.texts.supportTitle
        }
        if (this.googleAd.config.texts.supportMessage1 !== undefined) {
          delete this.googleAd.config.texts.supportMessage1
        }
        if (this.googleAd.config.texts.supportMessage2 !== undefined) {
          delete this.googleAd.config.texts.supportMessage2
        }
      }
      if (!Array.isArray(this.googleAd.config.textsOptions)) {
        this.$set(this.googleAd.config, 'textsOptions', [])
      }
      this.activeTextsOptionIndex = this.googleAd.config.textsOptions.length ? 0 : null

      // Initialize styles (defaults match phx-modal.js widget CSS, base 16px)
      if (!this.googleAd.config.styles) {
        this.$set(this.googleAd.config, 'styles', {
          title: { color: '#1a1a1a', fontSize: ['20px', '14px'] },
          message: { color: '#555555', fontSize: ['18px', '14px'] },
          timer: { color: '#6c757d', fontSize: ['14px', '14px'] },
          button: {
            color: '#ffffff',
            backgroundColor: '#1976d2',
            hoverBackgroundColor: '#1565c0',
            fontSize: ['16px', '16px'],
          },
        })
      } else {
        const defaultStyles = {
          title: { color: '#1a1a1a', fontSize: ['20px', '14px'] },
          message: { color: '#555555', fontSize: ['18px', '14px'] },
          timer: { color: '#6c757d', fontSize: ['14px', '14px'] },
          button: {
            color: '#ffffff',
            backgroundColor: '#1976d2',
            hoverBackgroundColor: '#1565c0',
            fontSize: ['16px', '16px'],
          },
        }
        Object.keys(defaultStyles).forEach(key => {
          if (!this.googleAd.config.styles[key]) {
            this.$set(this.googleAd.config.styles, key, defaultStyles[key])
          } else {
            if (!this.googleAd.config.styles[key].color) {
              this.$set(this.googleAd.config.styles[key], 'color', defaultStyles[key].color)
            }
            if (!this.googleAd.config.styles[key].fontSize) {
              this.$set(this.googleAd.config.styles[key], 'fontSize', defaultStyles[key].fontSize)
            }
            if (key === 'button') {
              if (!this.googleAd.config.styles.button.backgroundColor) {
                this.$set(this.googleAd.config.styles.button, 'backgroundColor', defaultStyles.button.backgroundColor)
              }
              if (!this.googleAd.config.styles.button.hoverBackgroundColor) {
                this.$set(this.googleAd.config.styles.button, 'hoverBackgroundColor', defaultStyles.button.hoverBackgroundColor)
              }
            }
          }
        })
      }

      this.$set(this.googleAd.config.styles, 'modal', normalizeModalSize(this.googleAd.config.styles.modal))

      this.$set(this.googleAd.config, 'targetings', normalizeGeoTargetings(this.googleAd.config.targetings))
      if (this.googleAd.config.enableImpressions == null) this.$set(this.googleAd.config, 'enableImpressions', false)

      const displayDefaults = {
        enableUserPercentage: false,
        userPercentage: 100,
        enablePageCounter: false,
        pageCounterThreshold: 3,
      }
      Object.keys(displayDefaults).forEach(key => {
        if (this.googleAd.config[key] == null) {
          this.$set(this.googleAd.config, key, displayDefaults[key])
        }
      })

      // Initialize feature flags. Keep fallback enabled for legacy records.
      if (this.googleAd.config.enableModal == null) {
        this.$set(this.googleAd.config, 'enableModal', true)
      }
      if (this.googleAd.config.enableInImage == null) {
        this.$set(this.googleAd.config, 'enableInImage', false)
      }
      if (this.googleAd.config.enableFallback == null) {
        this.$set(this.googleAd.config, 'enableFallback', true)
      }

      // Initialize In-Image settings
      if (!this.googleAd.config.inImage) {
        this.$set(this.googleAd.config, 'inImage', {
          contentSelector: '',
          maxOverlap: 30,
          frequencyCappingHours: 2,
          adSize: [[300, 250]],
          slots: [''],
        })
      } else if (this.googleAd.config.inImage.frequencyCappingHours === undefined) {
        this.$set(this.googleAd.config.inImage, 'frequencyCappingHours', this.googleAd.config.frequencyCappingHours ?? 2)
      }
      if (!this.googleAd.config.inImage.adSize) {
        this.$set(this.googleAd.config.inImage, 'adSize', [[300, 250]])
      }
      if (!this.googleAd.config.inImage.slots || !this.googleAd.config.inImage.slots.length) {
        this.$set(this.googleAd.config.inImage, 'slots', [''])
      }
    },
    async getUserData() {
      const responseData = await useJwt.getUser(this.$route.params.userId)
      this.account = responseData.data.account || []
    },
    addAdSize() {
      this.googleAd.config.gpt.adSize.push([300, 250])
    },
    addFluidSize() {
      if (!this.googleAd.config.gpt.adSize.includes('fluid')) {
        this.googleAd.config.gpt.adSize.push('fluid')
      }
    },
    removeAdSize(idx) {
      this.googleAd.config.gpt.adSize.splice(idx, 1)
    },
    extractUniqueAdSizes(scriptText) {
      const extracted = []
      const seen = new Set()

      // Extract numeric sizes like [300, 250]
      const sizePattern = /\[\s*(\d{2,5})\s*,\s*(\d{2,5})\s*\]/g
      let match = sizePattern.exec(scriptText)
      while (match) {
        const width = Number(match[1])
        const height = Number(match[2])
        const key = `${width}x${height}`

        if (width > 0 && height > 0 && !seen.has(key)) {
          seen.add(key)
          extracted.push([width, height])
        }

        match = sizePattern.exec(scriptText)
      }

      // Keep fluid if present in script
      if (/\bfluid\b/i.test(scriptText) && !seen.has('fluid')) {
        seen.add('fluid')
        extracted.push('fluid')
      }

      return extracted
    },
    fillAdSizesFromScript() {
      if (!this.isAdmin) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: this.$t('google_ads.gpt_extract_sizes_forbidden'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
          { root: true },
        )
        return
      }

      const scriptText = this.adSizesScriptInput?.trim()
      if (!scriptText) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: this.$t('google_ads.gpt_extract_sizes_empty'),
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
          { root: true },
        )
        return
      }

      const uniqueSizes = this.extractUniqueAdSizes(scriptText)
      if (!uniqueSizes.length) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: this.$t('google_ads.gpt_extract_sizes_not_found'),
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
          { root: true },
        )
        return
      }

      this.$set(this.googleAd.config.gpt, 'adSize', uniqueSizes)
      this.$store.dispatch(
        'error-store/showNotify',
        {
          title: this.$t('google_ads.gpt_extract_sizes_success', { count: uniqueSizes.length }),
          icon: 'CheckIcon',
          variant: 'success',
        },
        { root: true },
      )
    },
    addTextsOption() {
      this.googleAd.config.textsOptions.push({
        ...this.googleAd.config.texts,
        supportTitle: '',
        supportMessage1: '',
        supportMessage2: '',
      })
      this.activeTextsOptionIndex = this.googleAd.config.textsOptions.length - 1
    },
    removeTextsOption(index) {
      this.googleAd.config.textsOptions.splice(index, 1)
      this.activeTextsOptionIndex = this.googleAd.config.textsOptions.length
        ? Math.min(index, this.googleAd.config.textsOptions.length - 1)
        : null
    },
    async saveForm() {
      this.geoSubmitted = true
      if (this.googleAd.config?.enableModal !== false && Object.keys(geoTargetingErrors(this.googleAd.config?.targetings)).length) {
        this.activeMainTab = 0
        this.activeFormatTab = 0
        this.$refs.modalGeoTab.activate()
        return
      }
      this.displayConditionsSubmitted = true
      const success = await this.$refs.validationGoogleAdsRules.validate()
      if (modalSizeErrors(this.googleAd.config?.styles?.modal)) {
        this.activeMainTab = 0
        this.activeFormatTab = 0
        this.$refs.modalSettingsTabs.activateTab(this.$refs.modalStylesTab)
        return
      }
      if (!success || Object.keys(displayConditionErrors(this.googleAd.config)).length) return

      this.disabledBtn = true

      // Transform form data to API structure
      const apiPayload = formToApi(this.googleAd, {
        canEditPhoenixRate: this.isDashboardAdmin,
        canEditProviderSettings: this.isDashboardAdmin,
      })

      try {
        if (this.type === 'create') {
          const response = await useJwt.createGoogleWidget(apiPayload)
          if (response.data.googlewidget && response.data.googlewidget._id) {
            this.$store.dispatch(
              'error-store/showNotify',
              {
                title: this.successMessage('google_ads.created'),
                icon: 'CheckIcon',
                variant: 'success',
              },
              { root: true },
            )
            // Redirect to list page
            if (this.$route.params.userId) {
              const listPath = `/users/${this.$route.params.userId}/offerwall`
              if (this.$route.path !== listPath) {
                this.$router.push(listPath).catch(() => {
                  // Ignore navigation duplicate errors
                })
              }
            }
          }
        } else {
          await useJwt.updateGoogleWidget(this.googleAd._id, apiPayload)
          this.$store.dispatch(
            'error-store/showNotify',
            {
              title: this.successMessage('google_ads.updated'),
              icon: 'EditIcon',
              variant: 'success',
            },
            { root: true },
          )
          // Redirect to list page
          if (this.$route.params.userId) {
            const listPath = `/users/${this.$route.params.userId}/offerwall`
            if (this.$route.path !== listPath) {
              this.$router.push(listPath).catch(() => {
                // Ignore navigation duplicate errors
              })
            }
          }
        }
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: this.$t('form.error'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
          { root: true },
        )
      } finally {
        this.disabledBtn = false
      }
    },
    successMessage(key) {
      return String(this.$t(key)).replace(/\s*\([^)]*\)\s*$/, '')
    },
    openInImagePlatformSettings() {
      this.activeMainTab = 1
      this.$nextTick(() => {
        this.activePlatformFormatTab = this.googleAd.config.enableModal ? 1 : 0
      })
    },
    parsePx(val) {
      if (!val) return 0
      return parseInt(val, 10) || 0
    },
    updateFontSize(arr, index, val) {
      this.$set(arr, index, `${val}px`)
    },
    updateColor(element, val) {
      if (!this.googleAd.config.styles) {
        this.$set(this.googleAd.config, 'styles', {})
      }
      if (!this.googleAd.config.styles[element]) {
        this.$set(this.googleAd.config.styles, element, {})
      }
      this.$set(this.googleAd.config.styles[element], 'color', val)
    },
    updateButtonBg(val) {
      if (!this.googleAd.config.styles.button) {
        this.$set(this.googleAd.config.styles, 'button', {})
      }
      this.$set(this.googleAd.config.styles.button, 'backgroundColor', val)
    },
    updateButtonHoverBg(val) {
      if (!this.googleAd.config.styles.button) {
        this.$set(this.googleAd.config.styles, 'button', {})
      }
      this.$set(this.googleAd.config.styles.button, 'hoverBackgroundColor', val)
    },
    // In-Image slot management
    addInImageSlot() {
      if (this.googleAd.config.inImage.slots.length < 3) {
        this.googleAd.config.inImage.slots.push('')
      }
    },
    removeInImageSlot(idx) {
      if (this.googleAd.config.inImage.slots.length > 1) {
        this.googleAd.config.inImage.slots.splice(idx, 1)
      }
    },
    // In-Image ad size management
    addInImageAdSize() {
      this.googleAd.config.inImage.adSize.push([300, 250])
    },
    addInImageFluidSize() {
      if (!this.googleAd.config.inImage.adSize.includes('fluid')) {
        this.googleAd.config.inImage.adSize.push('fluid')
      }
    },
    removeInImageAdSize(idx) {
      this.googleAd.config.inImage.adSize.splice(idx, 1)
    },
    fillInImageAdSizesFromScript() {
      if (!this.isAdmin) return

      const scriptText = this.inImageAdSizesScriptInput?.trim()
      if (!scriptText) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: this.$t('google_ads.gpt_extract_sizes_empty'),
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
          { root: true },
        )
        return
      }

      const uniqueSizes = this.extractUniqueAdSizes(scriptText)
      if (!uniqueSizes.length) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: this.$t('google_ads.gpt_extract_sizes_not_found'),
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
          { root: true },
        )
        return
      }

      this.$set(this.googleAd.config.inImage, 'adSize', uniqueSizes)
      this.$store.dispatch(
        'error-store/showNotify',
        {
          title: this.$t('google_ads.gpt_extract_sizes_success', { count: uniqueSizes.length }),
          icon: 'CheckIcon',
          variant: 'success',
        },
        { root: true },
      )
    },
  },
}
</script>

<style scoped>
.site-domain-control {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 0.5rem;
}

.site-domain-control > .input-group {
  flex: 1 1 16rem;
  min-width: 0;
}

pre {
  max-height: 400px;
  overflow-y: auto;
}

.custom-color-picker[type=color].form-control {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  width: 100%;
  height: 40px;
  background-color: #fff;
  border: 1px solid #d8d6de;
  border-radius: 0.357rem;
      padding: 4px 5px;
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.custom-color-picker:focus {
  border-color: #7367f0;
  box-shadow: 0 3px 8px 0 rgba(115, 103, 240, 0.14);
}

.custom-color-picker::-webkit-color-swatch-wrapper {
  padding: 0;
  border-radius: 0.25rem;
}

.custom-color-picker::-webkit-color-swatch {
  border: none;
  border-radius: 0.25rem;
}

.custom-color-picker::-moz-color-swatch {
  border: none;
  border-radius: 0.25rem;
}

.sticky-preview {
  position: -webkit-sticky;
  position: sticky;
  top: 6.5rem;
  z-index: 10;
}

.preview-device-wrapper {
  margin: 0 auto;
  transition: all 0.3s ease;
  background-color: #f8f9fa;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.preview-device-wrapper--mobile {
  width: 340px;
  height: 600px;
  min-height: 600px;
  border: 12px solid #2b2b2b;
  border-radius: 36px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  padding: 15px;
  position: relative;
  overflow-y: auto;
}

.preview-device-wrapper--mobile::before {
  content: '';
  position: absolute;
  top: 6px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 4px;
  background-color: #2b2b2b;
  border-radius: 2px;
  z-index: 12;
}

.preview-device-wrapper--desktop {
  width: 100%;
}

.preview-timer {
  position: absolute;
  top: 15px;
  right: 20px;
  line-height: 1;
}

.mock-in-image-ad.top-left {
  top: 10px;
  left: 10px;
}
.mock-in-image-ad.top-right {
  top: 10px;
  right: 10px;
}
.mock-in-image-ad.bottom-left {
  bottom: 10px;
  left: 10px;
}
.mock-in-image-ad.bottom-right {
  bottom: 10px;
  right: 10px;
}
.mock-in-image-ad.bottom-center {
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
}
</style>

<style lang="scss">
.nav-tabs-custom {
  .nav-link {
    font-weight: 500;
  }
}
</style>
